import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, Users, HelpCircle, Megaphone, LogOut, Plus, Edit2, Trash2, 
  Save, X, ChevronDown, ChevronUp, Eye, EyeOff, Home, Briefcase,
  Check, AlertCircle, Loader2, Menu, ArrowLeft, FileText, ShoppingCart,
  UserCheck, Calendar, Mail, Phone, MapPin, Send, Clock, Settings, Zap,
  ScrollText, CreditCard, RefreshCw
} from 'lucide-react';
import RichTextEditor from './components/RichTextEditor';
import PackageManager from './components/PackageManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Irish Counties
const IRISH_COUNTIES = [
  "Antrim", "Armagh", "Carlow", "Cavan", "Clare", "Cork", "Derry", "Donegal",
  "Down", "Dublin", "Fermanagh", "Galway", "Kerry", "Kildare", "Kilkenny",
  "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath",
  "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Tyrone",
  "Waterford", "Westmeath", "Wexford", "Wicklow"
];

// ============== AUTH CONTEXT ==============
const AdminContext = React.createContext(null);

const useAdmin = () => {
  const context = React.useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

// ============== API HELPER ==============
const adminApi = {
  async login(username, password) {
    const res = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!res.ok) throw new Error('Invalid credentials');
    return res.json();
  },

  async verify(token) {
    const res = await fetch(`${API_URL}/api/admin/verify?token=${token}`);
    return res.ok;
  },

  // Packages
  async getPackages(token) {
    const res = await fetch(`${API_URL}/api/admin/packages?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch packages');
    return res.json();
  },
  async createPackage(token, data) {
    const res = await fetch(`${API_URL}/api/admin/packages?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create package');
    return res.json();
  },
  async updatePackage(token, id, data) {
    const res = await fetch(`${API_URL}/api/admin/packages/${id}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update package');
    return res.json();
  },
  async deletePackage(token, id) {
    const res = await fetch(`${API_URL}/api/admin/packages/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete package');
    return res.json();
  },

  // Testimonials
  async getTestimonials(token) {
    const res = await fetch(`${API_URL}/api/admin/testimonials?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch testimonials');
    return res.json();
  },
  async createTestimonial(token, data) {
    const res = await fetch(`${API_URL}/api/admin/testimonials?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create testimonial');
    return res.json();
  },
  async updateTestimonial(token, id, data) {
    const res = await fetch(`${API_URL}/api/admin/testimonials/${id}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update testimonial');
    return res.json();
  },
  async deleteTestimonial(token, id) {
    const res = await fetch(`${API_URL}/api/admin/testimonials/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete testimonial');
    return res.json();
  },

  // FAQs
  async getFaqs(token) {
    const res = await fetch(`${API_URL}/api/admin/faqs?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch FAQs');
    return res.json();
  },
  async createFaq(token, data) {
    const res = await fetch(`${API_URL}/api/admin/faqs?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create FAQ');
    return res.json();
  },
  async updateFaq(token, id, data) {
    const res = await fetch(`${API_URL}/api/admin/faqs/${id}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update FAQ');
    return res.json();
  },
  async deleteFaq(token, id) {
    const res = await fetch(`${API_URL}/api/admin/faqs/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete FAQ');
    return res.json();
  },

  // Banners
  async getBanners(token) {
    const res = await fetch(`${API_URL}/api/admin/banners?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch banners');
    return res.json();
  },
  async createBanner(token, data) {
    const res = await fetch(`${API_URL}/api/admin/banners?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create banner');
    return res.json();
  },
  async updateBanner(token, id, data) {
    const res = await fetch(`${API_URL}/api/admin/banners/${id}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update banner');
    return res.json();
  },
  async deleteBanner(token, id) {
    const res = await fetch(`${API_URL}/api/admin/banners/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete banner');
    return res.json();
  },

  // Legal Pages
  async getLegalPages(token) {
    const res = await fetch(`${API_URL}/api/admin/legal-pages?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch legal pages');
    return res.json();
  },
  async getLegalPage(token, slug) {
    const res = await fetch(`${API_URL}/api/admin/legal-pages/${slug}?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch legal page');
    return res.json();
  },
  async createLegalPage(token, data) {
    const res = await fetch(`${API_URL}/api/admin/legal-pages?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create legal page');
    return res.json();
  },
  async updateLegalPage(token, slug, data) {
    const res = await fetch(`${API_URL}/api/admin/legal-pages/${slug}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update legal page');
    return res.json();
  },
  async deleteLegalPage(token, slug) {
    const res = await fetch(`${API_URL}/api/admin/legal-pages/${slug}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete legal page');
    return res.json();
  },

  // Orders
  async getOrders(token, status = null) {
    const url = status 
      ? `${API_URL}/api/orders/admin/list?token=${token}&status=${status}`
      : `${API_URL}/api/orders/admin/list?token=${token}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch orders');
    return res.json();
  },
  async updateOrder(token, orderId, data) {
    const res = await fetch(`${API_URL}/api/orders/admin/${orderId}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update order');
    return res.json();
  },
  async sendAgreement(token, orderId) {
    const res = await fetch(`${API_URL}/api/orders/admin/${orderId}/send-agreement?token=${token}`, {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Failed to send agreement');
    return res.json();
  },
  async createOrder(token, orderData) {
    const res = await fetch(`${API_URL}/api/orders/admin/create?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to create order');
    }
    return res.json();
  },
  async deleteOrder(token, orderId) {
    const res = await fetch(`${API_URL}/api/orders/admin/${orderId}?token=${token}`, {
      method: 'DELETE'
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to delete order');
    }
    return res.json();
  },
  async resendOrderConfirmation(token, orderId) {
    const res = await fetch(`${API_URL}/api/orders/admin/${orderId}/resend-confirmation?token=${token}`, {
      method: 'POST'
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to resend confirmation');
    }
    return res.json();
  },

  // Subscribers
  async getCustomers(token, status = null) {
    const url = status 
      ? `${API_URL}/api/orders/admin/customers?token=${token}&status=${status}`
      : `${API_URL}/api/orders/admin/customers?token=${token}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch customers');
    return res.json();
  },

  // Integration Settings
  async getIntegrationSettings(token) {
    const res = await fetch(`${API_URL}/api/orders/admin/settings/integrations?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch settings');
    return res.json();
  },
  async updateIntegrationSettings(token, settings) {
    const res = await fetch(`${API_URL}/api/orders/admin/settings/integrations?token=${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    });
    if (!res.ok) throw new Error('Failed to update settings');
    return res.json();
  },

  // Customer Management
  async getCustomer(token, customerId) {
    const res = await fetch(`${API_URL}/api/orders/admin/customers/${customerId}?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch customer');
    return res.json();
  },
  async updateCustomer(token, customerId, data) {
    const res = await fetch(`${API_URL}/api/orders/admin/customers/${customerId}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update customer');
    return res.json();
  },

  // Communication Templates
  async getTemplates(token) {
    const res = await fetch(`${API_URL}/api/orders/admin/templates?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch templates');
    return res.json();
  },
  async updateTemplate(token, templateKey, data) {
    const res = await fetch(`${API_URL}/api/orders/admin/templates/${templateKey}?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update template');
    return res.json();
  },
  async resetTemplates(token, templateKey = null) {
    const url = templateKey 
      ? `${API_URL}/api/orders/admin/templates/reset?token=${token}&template_key=${templateKey}`
      : `${API_URL}/api/orders/admin/templates/reset?token=${token}`;
    const res = await fetch(url, {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Failed to reset template(s)');
    return res.json();
  },

  // Agreement
  async getAgreement(token) {
    const res = await fetch(`${API_URL}/api/admin/agreement?token=${token}`);
    if (!res.ok) throw new Error('Failed to fetch agreement');
    return res.json();
  },
  async updateAgreement(token, data) {
    const res = await fetch(`${API_URL}/api/admin/agreement?token=${token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update agreement');
    return res.json();
  },
  async resetAgreement(token) {
    const res = await fetch(`${API_URL}/api/admin/agreement/reset?token=${token}`, {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Failed to reset agreement');
    return res.json();
  }
};

// ============== COMPONENTS ==============

// Toast Notification
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      {message}
    </div>
  );
};

// Login Screen
const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await adminApi.login(username, password);
      onLogin(result.token, result.username);
    } catch (err) {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/images/geekbroadband-logo.webp" 
              alt="GeekBroadband" 
              className="h-14 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl items-center justify-center">
              <Package className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
          <p className="text-slate-600 mt-1">Sign in to manage your content</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
              placeholder="Enter username"
              required
              data-testid="admin-username-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition"
              placeholder="Enter password"
              required
              data-testid="admin-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-slate-500 hover:text-green-600 flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

// Sidebar Navigation
const Sidebar = ({ activeTab, setActiveTab, username, onLogout, mobileOpen, setMobileOpen }) => {
  const navItems = [
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'subscribers', label: 'Subscribers', icon: UserCheck },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'agreement', label: 'Agreement', icon: ScrollText },
    { id: 'testimonials', label: 'Testimonials', icon: Users },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'banners', label: 'Banners', icon: Megaphone },
    { id: 'legal', label: 'Legal Pages', icon: FileText },
    { id: 'communications', label: 'Communications', icon: Send },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 w-64 bg-slate-900 transform transition-transform duration-200 z-50 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <img 
                src="/images/geekbroadband-logo.webp" 
                alt="GeekBroadband" 
                className="h-10 object-contain brightness-0 invert"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-white">GeekBroadband</h1>
                  <p className="text-xs text-slate-400">Admin Panel</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                  activeTab === item.id
                    ? 'bg-green-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-white">{username?.[0]?.toUpperCase()}</span>
                </div>
                <span className="text-sm text-slate-300">{username}</span>
              </div>
              <button
                onClick={onLogout}
                className="text-slate-400 hover:text-red-400 transition"
                title="Logout"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};


// Testimonial Management
const TestimonialManager = ({ token, showToast }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    location: '',
    rating: 5,
    content: '',
    package_name: '',
    active: true,
    sort_order: 0
  });

  const loadTestimonials = useCallback(async () => {
    try {
      const data = await adminApi.getTestimonials(token);
      setTestimonials(data);
    } catch (err) {
      showToast('Failed to load testimonials', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await adminApi.updateTestimonial(token, editingId, formData);
        showToast('Testimonial updated successfully');
      } else {
        await adminApi.createTestimonial(token, formData);
        showToast('Testimonial created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadTestimonials();
    } catch (err) {
      showToast('Failed to save testimonial', 'error');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      customer_name: item.customer_name,
      location: item.location,
      rating: item.rating,
      content: item.content,
      package_name: item.package_name || '',
      active: item.active,
      sort_order: item.sort_order || 0
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await adminApi.deleteTestimonial(token, id);
      showToast('Testimonial deleted successfully');
      loadTestimonials();
    } catch (err) {
      showToast('Failed to delete testimonial', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      location: '',
      rating: 5,
      content: '',
      package_name: '',
      active: true,
      sort_order: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Testimonials</h2>
          <p className="text-slate-600">Manage customer reviews and testimonials</p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-5 h-5" />
          Add Testimonial
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">
                {editingId ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="e.g., Dublin, Ireland"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rating (1-5 stars)</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className={`text-2xl transition ${
                        star <= formData.rating ? 'text-yellow-400' : 'text-slate-300'
                      }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Testimonial Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Package Name (optional)</label>
                <input
                  type="text"
                  value={formData.package_name}
                  onChange={(e) => setFormData({ ...formData, package_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="e.g., Home Fibre 500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, active: !formData.active })}
                    className={`w-12 h-6 rounded-full transition ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${formData.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                  <span className="text-sm text-slate-700">{formData.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List */}
      {testimonials.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No testimonials yet</h3>
          <p className="text-slate-600 mt-1">Add your first customer testimonial</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {testimonials.map((item) => (
            <div key={item.id} className={`bg-white rounded-xl border p-6 ${item.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900">{item.customer_name}</h3>
                    <span className="text-slate-500 text-sm">{item.location}</span>
                    <div className="text-yellow-400">{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</div>
                  </div>
                  <p className="text-slate-600 line-clamp-2">{item.content}</p>
                  {item.package_name && <span className="text-xs text-green-600 mt-2 inline-block">{item.package_name}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition">
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// FAQ Management
const FAQManager = ({ token, showToast }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    active: true,
    sort_order: 0
  });

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'billing', label: 'Billing' },
    { id: 'installation', label: 'Installation' },
    { id: 'technical', label: 'Technical' }
  ];

  const loadFaqs = useCallback(async () => {
    try {
      const data = await adminApi.getFaqs(token);
      setFaqs(data);
    } catch (err) {
      showToast('Failed to load FAQs', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await adminApi.updateFaq(token, editingId, formData);
        showToast('FAQ updated successfully');
      } else {
        await adminApi.createFaq(token, formData);
        showToast('FAQ created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadFaqs();
    } catch (err) {
      showToast('Failed to save FAQ', 'error');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      question: item.question,
      answer: item.answer,
      category: item.category,
      active: item.active,
      sort_order: item.sort_order || 0
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await adminApi.deleteFaq(token, id);
      showToast('FAQ deleted successfully');
      loadFaqs();
    } catch (err) {
      showToast('Failed to delete FAQ', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', category: 'general', active: true, sort_order: 0 });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">FAQs</h2>
          <p className="text-slate-600">Manage frequently asked questions</p>
        </div>
        <button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition" data-testid="add-faq-btn">
          <Plus className="w-5 h-5" />
          Add FAQ
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Question</label>
                <input type="text" value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Answer</label>
                <textarea value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" rows={4} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none">
                  {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" min="0" />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <button type="button" onClick={() => setFormData({ ...formData, active: !formData.active })} className={`w-12 h-6 rounded-full transition ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${formData.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                  <span className="text-sm text-slate-700">{formData.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"><Save className="w-4 h-4" />{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {faqs.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <HelpCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No FAQs yet</h3>
          <p className="text-slate-600 mt-1">Add your first FAQ</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {faqs.map((item) => (
            <div key={item.id} className={`bg-white rounded-xl border p-6 ${item.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      item.category === 'billing' ? 'bg-purple-100 text-purple-700' :
                      item.category === 'installation' ? 'bg-blue-100 text-blue-700' :
                      item.category === 'technical' ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{item.category}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.question}</h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{item.answer}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"><Edit2 className="w-5 h-5" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Banner Management
const BannerManager = ({ token, showToast }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    banner_type: 'info',
    link_url: '',
    link_text: '',
    background_color: '#10B981',
    text_color: '#FFFFFF',
    start_date: '',
    end_date: '',
    active: true,
    sort_order: 0
  });

  const bannerTypes = [
    { id: 'info', label: 'Info', color: 'bg-blue-500' },
    { id: 'warning', label: 'Warning', color: 'bg-yellow-500' },
    { id: 'promotion', label: 'Promotion', color: 'bg-green-500' },
    { id: 'announcement', label: 'Announcement', color: 'bg-purple-500' }
  ];

  const loadBanners = useCallback(async () => {
    try {
      const data = await adminApi.getBanners(token);
      setBanners(data);
    } catch (err) {
      showToast('Failed to load banners', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (!submitData.start_date) delete submitData.start_date;
      if (!submitData.end_date) delete submitData.end_date;
      
      if (editingId) {
        await adminApi.updateBanner(token, editingId, submitData);
        showToast('Banner updated successfully');
      } else {
        await adminApi.createBanner(token, submitData);
        showToast('Banner created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadBanners();
    } catch (err) {
      showToast('Failed to save banner', 'error');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      content: item.content,
      banner_type: item.banner_type,
      link_url: item.link_url || '',
      link_text: item.link_text || '',
      background_color: item.background_color || '#10B981',
      text_color: item.text_color || '#FFFFFF',
      start_date: item.start_date ? item.start_date.split('T')[0] : '',
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      active: item.active,
      sort_order: item.sort_order || 0
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await adminApi.deleteBanner(token, id);
      showToast('Banner deleted successfully');
      loadBanners();
    } catch (err) {
      showToast('Failed to delete banner', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', content: '', banner_type: 'info', link_url: '', link_text: '',
      background_color: '#10B981', text_color: '#FFFFFF', start_date: '', end_date: '',
      active: true, sort_order: 0
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Banners & Announcements</h2>
          <p className="text-slate-600">Manage promotional banners and site announcements</p>
        </div>
        <button onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition" data-testid="add-banner-btn">
          <Plus className="w-5 h-5" />
          Add Banner
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{editingId ? 'Edit Banner' : 'Add New Banner'}</h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" rows={3} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                <div className="flex flex-wrap gap-2">
                  {bannerTypes.map((type) => (
                    <button key={type.id} type="button" onClick={() => setFormData({ ...formData, banner_type: type.id })}
                      className={`px-4 py-2 rounded-lg border transition ${formData.banner_type === type.id ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300 text-slate-600'}`}>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Background Color</label>
                  <input type="color" value={formData.background_color} onChange={(e) => setFormData({ ...formData, background_color: e.target.value })} className="w-full h-10 rounded-lg cursor-pointer" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Text Color</label>
                  <input type="color" value={formData.text_color} onChange={(e) => setFormData({ ...formData, text_color: e.target.value })} className="w-full h-10 rounded-lg cursor-pointer" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Link URL (optional)</label>
                  <input type="url" value={formData.link_url} onChange={(e) => setFormData({ ...formData, link_url: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Link Text</label>
                  <input type="text" value={formData.link_text} onChange={(e) => setFormData({ ...formData, link_text: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Learn more" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date (optional)</label>
                  <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date (optional)</label>
                  <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Sort Order</label>
                  <input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" min="0" />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <button type="button" onClick={() => setFormData({ ...formData, active: !formData.active })} className={`w-12 h-6 rounded-full transition ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${formData.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                  <span className="text-sm text-slate-700">{formData.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              
              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Preview</label>
                <div className="rounded-lg p-4" style={{ backgroundColor: formData.background_color, color: formData.text_color }}>
                  <strong>{formData.title || 'Banner Title'}</strong>
                  <p className="text-sm mt-1">{formData.content || 'Banner content goes here...'}</p>
                  {formData.link_text && <span className="text-sm underline mt-2 inline-block">{formData.link_text}</span>}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"><Save className="w-4 h-4" />{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {banners.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Megaphone className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No banners yet</h3>
          <p className="text-slate-600 mt-1">Create your first promotional banner</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {banners.map((item) => (
            <div key={item.id} className={`bg-white rounded-xl border p-6 ${item.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${
                      item.banner_type === 'warning' ? 'bg-yellow-500' :
                      item.banner_type === 'promotion' ? 'bg-green-500' :
                      item.banner_type === 'announcement' ? 'bg-purple-500' : 'bg-blue-500'
                    }`}>{item.banner_type}</span>
                    {!item.active && <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">Inactive</span>}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.content}</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    {item.start_date && <span>Starts: {new Date(item.start_date).toLocaleDateString()}</span>}
                    {item.end_date && <span>Ends: {new Date(item.end_date).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded" style={{ backgroundColor: item.background_color }} title="Background color" />
                  <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"><Edit2 className="w-5 h-5" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Agreement Manager
const AgreementManager = ({ token, showToast }) => {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    version: '1.0',
    direct_debit_terms: '',
    active: true
  });

  // Available merge fields for reference
  const mergeFields = [
    { field: '{{customer_name}}', description: 'Full customer name' },
    { field: '{{customer_email}}', description: 'Customer email' },
    { field: '{{customer_phone}}', description: 'Customer phone' },
    { field: '{{address_line1}}', description: 'Address line 1' },
    { field: '{{address_line2}}', description: 'Address line 2' },
    { field: '{{city}}', description: 'City' },
    { field: '{{county}}', description: 'County' },
    { field: '{{eircode}}', description: 'Eircode' },
    { field: '{{order_number}}', description: 'Order number' },
    { field: '{{order_date}}', description: 'Order date' },
    { field: '{{service_type}}', description: 'Home or Business' },
    { field: '{{package_name}}', description: 'Package name' },
    { field: '{{package_price}}', description: 'Package monthly price' },
    { field: '{{download_speed}}', description: 'Download speed in Mbps' },
    { field: '{{upload_speed}}', description: 'Upload speed in Mbps' },
    { field: '{{addons}}', description: 'Selected add-ons list' },
    { field: '{{total_monthly}}', description: 'Total monthly cost' },
    { field: '{{total_one_time}}', description: 'One-time fees' },
  ];

  const loadAgreement = useCallback(async () => {
    try {
      const data = await adminApi.getAgreement(token);
      setAgreement(data);
      setFormData({
        title: data.title || 'Broadband Service Agreement',
        content: data.content || '',
        version: data.version || '1.0',
        direct_debit_terms: data.direct_debit_terms || '',
        active: data.active !== false
      });
    } catch (err) {
      showToast('Failed to load agreement', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadAgreement();
  }, [loadAgreement]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateAgreement(token, formData);
      showToast('Agreement saved successfully');
      loadAgreement();
    } catch (err) {
      showToast('Failed to save agreement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the agreement to default? This cannot be undone.')) return;
    try {
      await adminApi.resetAgreement(token);
      showToast('Agreement reset to default');
      loadAgreement();
    } catch (err) {
      showToast('Failed to reset agreement', 'error');
    }
  };

  const insertMergeField = (field, target) => {
    const textarea = document.getElementById(target);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = target === 'agreement-content' ? formData.content : formData.direct_debit_terms;
      const newValue = value.substring(0, start) + field + value.substring(end);
      
      if (target === 'agreement-content') {
        setFormData({ ...formData, content: newValue });
      } else {
        setFormData({ ...formData, direct_debit_terms: newValue });
      }
      
      // Reset cursor position after state update
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.length, start + field.length);
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Broadband Agreement</h2>
          <p className="text-slate-600">Manage the service agreement and direct debit mandate</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
            data-testid="reset-agreement-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            data-testid="save-agreement-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Agreement
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">About Merge Fields</h4>
            <p className="text-sm text-blue-700 mt-1">
              Use merge fields like <code className="bg-blue-100 px-1 rounded">{"{{customer_name}}"}</code> to automatically 
              insert customer and order details when the agreement is viewed in the customer portal.
            </p>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Agreement Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Agreement Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              data-testid="agreement-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="e.g., 1.0, 2.1"
            />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, active: !formData.active })}
              className={`relative w-11 h-6 rounded-full transition ${
                formData.active ? 'bg-green-500' : 'bg-slate-300'
              }`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                formData.active ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
            <span className="text-sm text-slate-700">{formData.active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      {/* Merge Fields Reference */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-slate-500" />
          Available Merge Fields
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {mergeFields.map((mf) => (
            <div
              key={mf.field}
              className="flex items-center justify-between p-2 bg-slate-50 rounded-lg group"
            >
              <div className="flex-1 min-w-0">
                <code className="text-xs text-green-600 font-mono block truncate">{mf.field}</code>
                <span className="text-xs text-slate-500 truncate block">{mf.description}</span>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(mf.field);
                  showToast('Copied to clipboard');
                }}
                className="ml-2 p-1 text-slate-400 hover:text-green-600 opacity-0 group-hover:opacity-100 transition"
                title="Copy to clipboard"
              >
                <Edit2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Agreement Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-slate-500" />
            Service Agreement Content
          </h3>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          This is the main agreement document. Use the editor below to format and add merge fields.
        </p>
        <div data-testid="agreement-content-editor">
          <RichTextEditor
            value={formData.content}
            onChange={(value) => setFormData({ ...formData, content: value })}
            placeholder="Enter agreement content with formatting..."
            minHeight="400px"
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Tip: Click on merge fields above to copy them, then paste into the editor where needed.
        </p>
      </div>

      {/* Direct Debit Terms */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Direct Debit Mandate Terms
          </h3>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          SEPA Direct Debit mandate information displayed alongside the agreement.
        </p>
        <div data-testid="direct-debit-terms-editor">
          <RichTextEditor
            value={formData.direct_debit_terms}
            onChange={(value) => setFormData({ ...formData, direct_debit_terms: value })}
            placeholder="Enter direct debit mandate terms..."
            minHeight="250px"
          />
        </div>
      </div>

      {/* Preview Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Eye className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">Preview</h4>
            <p className="text-sm text-amber-700 mt-1">
              To see how the agreement looks with real data, view it from the Customer Portal after a customer 
              has placed an order and received the agreement signing email.
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      {agreement?.last_updated && (
        <p className="text-sm text-slate-500 text-center">
          Last updated: {new Date(agreement.last_updated).toLocaleString()}
        </p>
      )}
    </div>
  );
};

// Legal Pages Management
const LegalPagesManager = ({ token, showToast }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlug, setEditingSlug] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    content: '',
    meta_description: '',
    active: true
  });

  const pageTemplates = [
    { slug: 'privacy-policy', title: 'Privacy Policy' },
    { slug: 'terms-of-service', title: 'Terms of Service' },
    { slug: 'cookie-policy', title: 'Cookie Policy' },
    { slug: 'modern-slavery', title: 'Modern Slavery & Human Trafficking Statement' }
  ];

  const loadPages = useCallback(async () => {
    try {
      const data = await adminApi.getLegalPages(token);
      setPages(data);
    } catch (err) {
      showToast('Failed to load legal pages', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlug) {
        await adminApi.updateLegalPage(token, editingSlug, formData);
        showToast('Page updated successfully');
      } else {
        await adminApi.createLegalPage(token, formData);
        showToast('Page created successfully');
      }
      setShowForm(false);
      setEditingSlug(null);
      resetForm();
      loadPages();
    } catch (err) {
      showToast('Failed to save page', 'error');
    }
  };

  const handleEdit = (page) => {
    setFormData({
      slug: page.slug,
      title: page.title,
      content: page.content,
      meta_description: page.meta_description || '',
      active: page.active
    });
    setEditingSlug(page.slug);
    setShowForm(true);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await adminApi.deleteLegalPage(token, slug);
      showToast('Page deleted successfully');
      loadPages();
    } catch (err) {
      showToast('Failed to delete page', 'error');
    }
  };

  const handleCreateFromTemplate = (template) => {
    setFormData({
      slug: template.slug,
      title: template.title,
      content: '',
      meta_description: '',
      active: true
    });
    setEditingSlug(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      content: '',
      meta_description: '',
      active: true
    });
  };

  // Check which templates haven't been created yet
  const availableTemplates = pageTemplates.filter(
    template => !pages.some(page => page.slug === template.slug)
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Legal Pages</h2>
          <p className="text-slate-600">Manage your Privacy Policy, Terms of Service, and other legal documents</p>
        </div>
        {availableTemplates.length > 0 && (
          <div className="relative group">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              data-testid="add-legal-page-btn"
            >
              <Plus className="w-5 h-5" />
              Add Page
              <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              {availableTemplates.map((template) => (
                <button
                  key={template.slug}
                  onClick={() => handleCreateFromTemplate(template)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg text-slate-700"
                >
                  {template.title}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">{editingSlug ? 'Edit Page' : 'Create Page'}</h3>
              <button onClick={() => { setShowForm(false); setEditingSlug(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Page Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="e.g., privacy-policy"
                    disabled={!!editingSlug}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">URL will be: /legal/{formData.slug || 'slug'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Page Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="e.g., Privacy Policy"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Meta Description (for SEO)</label>
                <input
                  type="text"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="Brief description for search engines..."
                  maxLength={160}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content (HTML supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-mono text-sm"
                  rows={16}
                  placeholder="Enter your page content here. HTML tags are supported for formatting..."
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Tip: Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt; for formatting</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, active: !formData.active })}
                  className={`w-12 h-6 rounded-full transition ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition ${formData.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-sm text-slate-700">{formData.active ? 'Published' : 'Draft'}</span>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingSlug(null); }} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"><Save className="w-4 h-4" />{editingSlug ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pages.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No legal pages yet</h3>
          <p className="text-slate-600 mt-1 mb-4">Create your legal documents to display on your website</p>
          <div className="flex flex-wrap justify-center gap-2">
            {pageTemplates.map((template) => (
              <button
                key={template.slug}
                onClick={() => handleCreateFromTemplate(template)}
                className="px-4 py-2 bg-white border border-slate-300 rounded-lg hover:border-green-500 hover:text-green-600 transition text-sm"
              >
                Create {template.title}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {pages.map((page) => (
            <div key={page.slug} className={`bg-white rounded-xl border p-6 ${page.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900">{page.title}</h3>
                    {!page.active && <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Draft</span>}
                  </div>
                  <p className="text-sm text-slate-500 mb-2">/legal/{page.slug}</p>
                  {page.meta_description && (
                    <p className="text-sm text-slate-600 line-clamp-1">{page.meta_description}</p>
                  )}
                  {page.last_updated && (
                    <p className="text-xs text-slate-400 mt-2">Last updated: {new Date(page.last_updated).toLocaleDateString()}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/legal/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    title="View page"
                  >
                    <Eye className="w-5 h-5" />
                  </a>
                  <button onClick={() => handleEdit(page)} className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition">
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(page.slug)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Orders Management
const OrdersManager = ({ token, showToast }) => {
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Irish Counties
  const IRISH_COUNTIES = [
    "Antrim", "Armagh", "Carlow", "Cavan", "Clare", "Cork", "Derry", "Donegal",
    "Down", "Dublin", "Fermanagh", "Galway", "Kerry", "Kildare", "Kilkenny",
    "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath",
    "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Tyrone",
    "Waterford", "Westmeath", "Wexford", "Wicklow"
  ];
  
  const [newOrderForm, setNewOrderForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    service_type: 'home',
    business_info: { company_name: '', company_reg: '', vat_number: '', contact_name: '', contact_position: '' },
    installation_address: { address_line1: '', address_line2: '', city: '', county: '', eircode: '' },
    switching_info: { is_switching: false, current_provider: '', uan: '', account_number: '' },
    package_id: '',
    addons: { voip_landline: false },
    network_provider: '',
    status: 'new',
    admin_notes: '',
    customer_notes: '',
    uan: '',
    scheduled_install_date: '',
    scheduled_install_slot: '',
    send_notifications: true
  });

  const statusColors = {
    new: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-yellow-100 text-yellow-700',
    installation_scheduled: 'bg-purple-100 text-purple-700',
    active: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    new: 'New',
    confirmed: 'Confirmed',
    installation_scheduled: 'Install Scheduled',
    active: 'Active',
    cancelled: 'Cancelled'
  };

  const loadOrders = useCallback(async () => {
    try {
      const status = statusFilter !== 'all' ? statusFilter : null;
      const data = await adminApi.getOrders(token, status);
      setOrders(data.orders || []);
    } catch (err) {
      showToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast, statusFilter]);
  
  const loadPackages = useCallback(async () => {
    try {
      const data = await adminApi.getPackages(token);
      setPackages(data || []);
    } catch (err) {
      console.error('Failed to load packages');
    }
  }, [token]);

  useEffect(() => {
    loadOrders();
    loadPackages();
  }, [loadOrders, loadPackages]);

  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;
    try {
      // Clean up the form data - convert empty strings to null for optional fields
      const cleanedData = { ...editFormData };
      
      // Convert empty strings to null for fields that expect null or valid values
      if (cleanedData.scheduled_install_date === '') {
        cleanedData.scheduled_install_date = null;
      }
      if (cleanedData.uan === '') {
        cleanedData.uan = null;
      }
      if (cleanedData.cli === '') {
        cleanedData.cli = null;
      }
      if (cleanedData.modem_model === '') {
        cleanedData.modem_model = null;
      }
      if (cleanedData.modem_serial === '') {
        cleanedData.modem_serial = null;
      }
      if (cleanedData.ip_address_assigned === '') {
        cleanedData.ip_address_assigned = null;
      }
      if (cleanedData.scheduled_install_slot === '') {
        cleanedData.scheduled_install_slot = null;
      }
      if (cleanedData.admin_notes === '') {
        cleanedData.admin_notes = null;
      }
      if (cleanedData.blacknight_order_number === '') {
        cleanedData.blacknight_order_number = null;
      }
      if (cleanedData.broadband_username === '') {
        cleanedData.broadband_username = null;
      }
      
      await adminApi.updateOrder(token, selectedOrder.id, cleanedData);
      showToast('Order updated successfully');
      setSelectedOrder(null);
      loadOrders();
    } catch (err) {
      showToast('Failed to update order', 'error');
    }
  };

  const handleSendAgreement = async (orderId) => {
    try {
      await adminApi.sendAgreement(token, orderId);
      showToast('Agreement sent successfully');
      loadOrders();
    } catch (err) {
      showToast('Failed to send agreement', 'error');
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) {
      return;
    }
    try {
      await adminApi.deleteOrder(token, orderId);
      showToast('Order deleted successfully');
      loadOrders();
    } catch (err) {
      showToast(err.message || 'Failed to delete order', 'error');
    }
  };

  const handleResendConfirmation = async (orderId, email) => {
    try {
      await adminApi.resendOrderConfirmation(token, orderId);
      showToast(`Order confirmation re-sent to ${email}`);
    } catch (err) {
      showToast(err.message || 'Failed to resend confirmation', 'error');
    }
  };

  const openEditModal = (order) => {
    setSelectedOrder(order);
    setEditFormData({
      status: order.status,
      uan: order.uan || '',
      cli: order.cli || '',
      modem_model: order.modem_model || '',
      modem_serial: order.modem_serial || '',
      ip_address_assigned: order.ip_address_assigned || '',
      scheduled_install_date: order.scheduled_install_date ? order.scheduled_install_date.split('T')[0] : '',
      scheduled_install_slot: order.scheduled_install_slot || '',
      admin_notes: order.admin_notes || '',
      blacknight_order_number: order.blacknight_order_number || '',
      broadband_username: order.broadband_username || ''
    });
  };
  
  const resetNewOrderForm = () => {
    setNewOrderForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      service_type: 'home',
      business_info: { company_name: '', company_reg: '', vat_number: '', contact_name: '', contact_position: '' },
      installation_address: { address_line1: '', address_line2: '', city: '', county: '', eircode: '' },
      switching_info: { is_switching: false, current_provider: '', uan: '', account_number: '' },
      package_id: '',
      addons: { voip_landline: false },
      network_provider: '',
      status: 'new',
      admin_notes: '',
      customer_notes: '',
      uan: '',
      scheduled_install_date: '',
      scheduled_install_slot: '',
      send_notifications: true
    });
  };
  
  const handleCreateOrder = async () => {
    // Validate required fields
    if (!newOrderForm.first_name || !newOrderForm.last_name || !newOrderForm.email || !newOrderForm.phone) {
      showToast('Please fill in all customer details', 'error');
      return;
    }
    if (!newOrderForm.installation_address.address_line1 || !newOrderForm.installation_address.city || 
        !newOrderForm.installation_address.county || !newOrderForm.installation_address.eircode) {
      showToast('Please fill in the installation address', 'error');
      return;
    }
    if (!newOrderForm.package_id) {
      showToast('Please select a package', 'error');
      return;
    }
    if (newOrderForm.service_type === 'business' && !newOrderForm.business_info.company_name) {
      showToast('Please enter the company name for business orders', 'error');
      return;
    }
    
    setCreating(true);
    try {
      // Prepare the data
      const orderData = {
        ...newOrderForm,
        business_info: newOrderForm.service_type === 'business' ? newOrderForm.business_info : null,
        switching_info: newOrderForm.switching_info.is_switching ? {
          current_provider: newOrderForm.switching_info.current_provider,
          uan: newOrderForm.switching_info.uan,
          account_number: newOrderForm.switching_info.account_number
        } : null,
        scheduled_install_date: newOrderForm.scheduled_install_date || null
      };
      
      const result = await adminApi.createOrder(token, orderData);
      showToast(`Order ${result.order_number} created successfully`);
      setShowCreateModal(false);
      resetNewOrderForm();
      loadOrders();
    } catch (err) {
      showToast(err.message || 'Failed to create order', 'error');
    } finally {
      setCreating(false);
    }
  };
  
  const selectedPackage = packages.find(p => p.id === newOrderForm.package_id);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Orders</h2>
          <p className="text-slate-600">Manage customer orders and installations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          data-testid="create-order-btn"
        >
          <Plus className="w-4 h-4" />
          Create Order
        </button>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        {['all', 'new', 'confirmed', 'installation_scheduled', 'active', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === status
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {status === 'all' ? 'All Orders' : statusLabels[status]}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <ShoppingCart className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No orders yet</h3>
          <p className="text-slate-600 mt-1">Click "Create Order" to add one manually</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900">{order.order_number}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[order.status]}`}>
                      {statusLabels[order.status]}
                    </span>
                    {order.agreement_signed && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Signed</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      {order.first_name} {order.last_name}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      {order.email}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      {order.phone}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {order.installation_address?.eircode}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Package className="w-4 h-4" />
                      {order.package_name}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {new Date(order.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <span className="font-semibold text-green-600">€{(order.total_monthly || 0).toFixed(2)}/month <span className="text-slate-400 font-normal">(excl. VAT)</span></span>
                    {order.total_one_time > 0 && (
                      <span className="text-slate-500">+ €{(order.total_one_time || 0).toFixed(2)} one-time</span>
                    )}
                    {order.uan && <span className="text-slate-500">UAN: {order.uan}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleResendConfirmation(order.id, order.email)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition flex items-center gap-1"
                    title="Re-send order confirmation email"
                  >
                    <Mail className="w-4 h-4" />
                    Resend Confirmation
                  </button>
                  {!order.agreement_sent && order.status === 'confirmed' && (
                    <button
                      onClick={() => handleSendAgreement(order.id)}
                      className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition flex items-center gap-1"
                    >
                      <Send className="w-4 h-4" />
                      Send Agreement
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(order)}
                    className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition flex items-center gap-1"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteOrder(order.id, order.order_number)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Order Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Edit Order {selectedOrder.order_number}</h3>
              <button onClick={() => setSelectedOrder(null)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="new">New</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="installation_scheduled">Installation Scheduled</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UAN</label>
                  <input
                    type="text"
                    value={editFormData.uan}
                    onChange={(e) => setEditFormData({ ...editFormData, uan: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="Unique Access Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CLI</label>
                  <input
                    type="text"
                    value={editFormData.cli}
                    onChange={(e) => setEditFormData({ ...editFormData, cli: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="Caller Line Identification"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IP Address</label>
                  <input
                    type="text"
                    value={editFormData.ip_address_assigned}
                    onChange={(e) => setEditFormData({ ...editFormData, ip_address_assigned: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="Assigned IP"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Modem Model</label>
                  <input
                    type="text"
                    value={editFormData.modem_model}
                    onChange={(e) => setEditFormData({ ...editFormData, modem_model: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Modem Serial</label>
                  <input
                    type="text"
                    value={editFormData.modem_serial}
                    onChange={(e) => setEditFormData({ ...editFormData, modem_serial: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Scheduled Install Date</label>
                  <input
                    type="date"
                    value={editFormData.scheduled_install_date}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduled_install_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Install Time Slot</label>
                  <select
                    value={editFormData.scheduled_install_slot}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduled_install_slot: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="">Select slot...</option>
                    <option value="morning">Morning (9am - 12pm)</option>
                    <option value="afternoon">Afternoon (1pm - 5pm)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin Notes</label>
                <textarea
                  value={editFormData.admin_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, admin_notes: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  rows={3}
                />
              </div>
              
              {/* Blacknight/Provider Fields */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Blacknight Order Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Blacknight Order Number</label>
                    <input
                      type="text"
                      value={editFormData.blacknight_order_number}
                      onChange={(e) => setEditFormData({ ...editFormData, blacknight_order_number: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="e.g., BK-123456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Broadband Username</label>
                    <input
                      type="text"
                      value={editFormData.broadband_username}
                      onChange={(e) => setEditFormData({ ...editFormData, broadband_username: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="username@borrowageek.com"
                    />
                    <p className="text-xs text-slate-500 mt-1">Reference only - from Blacknight order process</p>
                  </div>
                </div>
              </div>
              
              {/* Agreement Status */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-2">Agreement Status</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Sent: {selectedOrder.agreement_sent ? `Yes (${new Date(selectedOrder.agreement_sent_at).toLocaleString()})` : 'No'}</p>
                  <p>Signed: {selectedOrder.agreement_signed ? `Yes (${new Date(selectedOrder.agreement_signed_at).toLocaleString()})` : 'No'}</p>
                  {selectedOrder.signature_ip && <p>Signature IP: {selectedOrder.signature_ip}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={() => setSelectedOrder(null)} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition">Cancel</button>
                <button onClick={handleUpdateOrder} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Order Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Create New Order</h3>
                <p className="text-sm text-slate-500">Fill in the customer and order details</p>
              </div>
              <button onClick={() => { setShowCreateModal(false); resetNewOrderForm(); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Service Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Service Type *</label>
                <div className="flex gap-4">
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition ${
                    newOrderForm.service_type === 'home' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="service_type"
                      value="home"
                      checked={newOrderForm.service_type === 'home'}
                      onChange={() => setNewOrderForm({ ...newOrderForm, service_type: 'home' })}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Home Broadband</span>
                    </div>
                  </label>
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition ${
                    newOrderForm.service_type === 'business' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="service_type"
                      value="business"
                      checked={newOrderForm.service_type === 'business'}
                      onChange={() => setNewOrderForm({ ...newOrderForm, service_type: 'business' })}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Business Broadband</span>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Customer Details */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Customer Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newOrderForm.first_name}
                      onChange={(e) => setNewOrderForm({ ...newOrderForm, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newOrderForm.last_name}
                      onChange={(e) => setNewOrderForm({ ...newOrderForm, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="Murphy"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newOrderForm.email}
                      onChange={(e) => setNewOrderForm({ ...newOrderForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={newOrderForm.phone}
                      onChange={(e) => setNewOrderForm({ ...newOrderForm, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="087 123 4567"
                    />
                  </div>
                </div>
              </div>
              
              {/* Business Info (conditional) */}
              {newOrderForm.service_type === 'business' && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Business Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        value={newOrderForm.business_info.company_name}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          business_info: { ...newOrderForm.business_info, company_name: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                        placeholder="Acme Ltd"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company Registration</label>
                      <input
                        type="text"
                        value={newOrderForm.business_info.company_reg}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          business_info: { ...newOrderForm.business_info, company_reg: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">VAT Number</label>
                      <input
                        type="text"
                        value={newOrderForm.business_info.vat_number}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          business_info: { ...newOrderForm.business_info, vat_number: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                        placeholder="IE1234567X"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Installation Address */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Installation Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                    <input
                      type="text"
                      value={newOrderForm.installation_address.address_line1}
                      onChange={(e) => setNewOrderForm({ 
                        ...newOrderForm, 
                        installation_address: { ...newOrderForm.installation_address, address_line1: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={newOrderForm.installation_address.address_line2}
                      onChange={(e) => setNewOrderForm({ 
                        ...newOrderForm, 
                        installation_address: { ...newOrderForm.installation_address, address_line2: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="Apartment 4B"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Town/City *</label>
                    <input
                      type="text"
                      value={newOrderForm.installation_address.city}
                      onChange={(e) => setNewOrderForm({ 
                        ...newOrderForm, 
                        installation_address: { ...newOrderForm.installation_address, city: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="Dublin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">County *</label>
                    <select
                      value={newOrderForm.installation_address.county}
                      onChange={(e) => setNewOrderForm({ 
                        ...newOrderForm, 
                        installation_address: { ...newOrderForm.installation_address, county: e.target.value }
                      })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    >
                      <option value="">Select County...</option>
                      {IRISH_COUNTIES.map((county) => (
                        <option key={county} value={county}>{county}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Eircode *</label>
                    <input
                      type="text"
                      value={newOrderForm.installation_address.eircode}
                      onChange={(e) => setNewOrderForm({ 
                        ...newOrderForm, 
                        installation_address: { ...newOrderForm.installation_address, eircode: e.target.value.toUpperCase() }
                      })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="D02 X123"
                    />
                  </div>
                </div>
              </div>
              
              {/* Switching Provider */}
              <div className="bg-slate-50 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={newOrderForm.switching_info.is_switching}
                    onChange={(e) => setNewOrderForm({ 
                      ...newOrderForm, 
                      switching_info: { ...newOrderForm.switching_info, is_switching: e.target.checked }
                    })}
                    className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="font-medium text-slate-900">Customer is switching from another provider</span>
                </label>
                
                {newOrderForm.switching_info.is_switching && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Current Provider</label>
                      <select
                        value={newOrderForm.switching_info.current_provider}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          switching_info: { ...newOrderForm.switching_info, current_provider: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      >
                        <option value="">Select...</option>
                        <option value="eir">Eir</option>
                        <option value="vodafone">Vodafone</option>
                        <option value="sky">Sky</option>
                        <option value="virgin">Virgin Media</option>
                        <option value="pure">Pure Telecom</option>
                        <option value="digiweb">Digiweb</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">UAN (if known)</label>
                      <input
                        type="text"
                        value={newOrderForm.switching_info.uan}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          switching_info: { ...newOrderForm.switching_info, uan: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                        placeholder="UAN123456"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Account Number</label>
                      <input
                        type="text"
                        value={newOrderForm.switching_info.account_number}
                        onChange={(e) => setNewOrderForm({ 
                          ...newOrderForm, 
                          switching_info: { ...newOrderForm.switching_info, account_number: e.target.value }
                        })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Package Selection */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Package Selection
                </h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Package *</label>
                  <select
                    value={newOrderForm.package_id}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, package_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="">Select a package...</option>
                    {packages
                      .filter(p => p.package_type === newOrderForm.service_type && p.active)
                      .map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} - {pkg.download_speed_mbps}/{pkg.upload_speed_mbps} Mbps - €{pkg.monthly_price}/month (excl. VAT)
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                {selectedPackage && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-green-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-900">{selectedPackage.name}</p>
                        <p className="text-sm text-slate-600">{selectedPackage.download_speed_mbps}/{selectedPackage.upload_speed_mbps} Mbps</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">€{selectedPackage.monthly_price}/month</p>
                        <p className="text-xs text-slate-400">excl. VAT</p>
                      </div>
                    </div>
                    
                    {/* Add-ons */}
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                      {selectedPackage.voip_available && (
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newOrderForm.addons.voip_landline}
                            onChange={(e) => setNewOrderForm({ 
                              ...newOrderForm, 
                              addons: { ...newOrderForm.addons, voip_landline: e.target.checked }
                            })}
                            className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm">
                            <Phone className="w-4 h-4 inline text-blue-600 mr-1" />
                            VOIP Landline (+€{selectedPackage.voip_price}/month)
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Network Provider */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Network Provider</label>
                  <select
                    value={newOrderForm.network_provider}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, network_provider: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="">Unknown/Not checked</option>
                    <option value="openeir">Open Eir (FTTH)</option>
                    <option value="siro">SIRO</option>
                    <option value="nbi">NBI (National Broadband Ireland)</option>
                    <option value="vdsl">VDSL</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Initial Status</label>
                  <select
                    value={newOrderForm.status}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  >
                    <option value="new">New</option>
                    <option value="confirmed">Confirmed (will send agreement)</option>
                    <option value="installation_scheduled">Installation Scheduled</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>
              
              {/* Admin Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UAN (if known)</label>
                  <input
                    type="text"
                    value={newOrderForm.uan}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, uan: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    placeholder="UAN123456"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Scheduled Install Date</label>
                  <input
                    type="date"
                    value={newOrderForm.scheduled_install_date}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, scheduled_install_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>
              
              {/* Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Customer Notes</label>
                  <textarea
                    value={newOrderForm.customer_notes}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, customer_notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    rows={3}
                    placeholder="Notes from customer..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Admin Notes</label>
                  <textarea
                    value={newOrderForm.admin_notes}
                    onChange={(e) => setNewOrderForm({ ...newOrderForm, admin_notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    rows={3}
                    placeholder="Internal notes..."
                  />
                </div>
              </div>
              
              {/* Notifications Toggle */}
              <label className="flex items-center gap-3 cursor-pointer bg-amber-50 rounded-lg p-4 border border-amber-200">
                <input
                  type="checkbox"
                  checked={newOrderForm.send_notifications}
                  onChange={(e) => setNewOrderForm({ ...newOrderForm, send_notifications: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-slate-700">
                  <strong>Send notifications:</strong> Email order confirmation to customer and alert admin
                  {newOrderForm.status === 'confirmed' && ' (will also send agreement for signing)'}
                </span>
              </label>
            </div>
            
            {/* Footer */}
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-xl">
              <button 
                onClick={() => { setShowCreateModal(false); resetNewOrderForm(); }} 
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateOrder}
                disabled={creating}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscribers Management
const SubscribersManager = ({ token, showToast }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCustomers = useCallback(async () => {
    try {
      const status = statusFilter !== 'all' ? statusFilter : null;
      const data = await adminApi.getCustomers(token, status);
      setCustomers(data.customers || []);
    } catch (err) {
      showToast('Failed to load subscribers', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast, statusFilter]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleEdit = (customer) => {
    setEditingCustomer({
      ...customer,
      installation_address: customer.installation_address || {
        address_line1: '',
        address_line2: '',
        city: '',
        county: '',
        eircode: ''
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateCustomer(token, editingCustomer.id, {
        first_name: editingCustomer.first_name,
        last_name: editingCustomer.last_name,
        email: editingCustomer.email,
        phone: editingCustomer.phone,
        business_name: editingCustomer.business_name,
        status: editingCustomer.status,
        installation_address: editingCustomer.installation_address
      });
      showToast('Customer updated successfully', 'success');
      setEditingCustomer(null);
      loadCustomers();
    } catch (err) {
      showToast('Failed to update customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Subscribers</h2>
        <p className="text-slate-600">Manage customer accounts and subscriptions</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'active', 'suspended', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === status
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <UserCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No subscribers yet</h3>
          <p className="text-slate-600 mt-1">Subscribers will appear here after orders are placed</p>
        </div>
      ) : (
        <div className="space-y-4">
          {customers.map((customer) => (
            <div key={customer.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900">{customer.first_name} {customer.last_name}</h3>
                    {customer.business_name && (
                      <span className="text-sm text-slate-500">({customer.business_name})</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      customer.status === 'active' ? 'bg-green-100 text-green-700' :
                      customer.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      customer.status === 'suspended' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {customer.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {customer.email}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {customer.phone}
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      {customer.package_name || 'No active package'}
                    </div>
                    {customer.installation_address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {customer.installation_address.eircode || 'No address'}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(customer)}
                  className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                  data-testid={`edit-customer-${customer.id}`}
                >
                  <Edit2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Edit Customer</h3>
              <button onClick={() => setEditingCustomer(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Personal Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editingCustomer.first_name || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editingCustomer.last_name || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name (if applicable)</label>
                <input
                  type="text"
                  value={editingCustomer.business_name || ''}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, business_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  placeholder="Leave blank for residential customers"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editingCustomer.email || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editingCustomer.phone || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={editingCustomer.status || 'pending'}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Installation Address */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Installation Address
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1</label>
                    <input
                      type="text"
                      value={editingCustomer.installation_address?.address_line1 || ''}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        installation_address: { ...editingCustomer.installation_address, address_line1: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={editingCustomer.installation_address?.address_line2 || ''}
                      onChange={(e) => setEditingCustomer({
                        ...editingCustomer,
                        installation_address: { ...editingCustomer.installation_address, address_line2: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                      <input
                        type="text"
                        value={editingCustomer.installation_address?.city || ''}
                        onChange={(e) => setEditingCustomer({
                          ...editingCustomer,
                          installation_address: { ...editingCustomer.installation_address, city: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">County</label>
                      <select
                        value={editingCustomer.installation_address?.county || ''}
                        onChange={(e) => setEditingCustomer({
                          ...editingCustomer,
                          installation_address: { ...editingCustomer.installation_address, county: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                      >
                        <option value="">Select County</option>
                        {IRISH_COUNTIES.map((county) => (
                          <option key={county} value={county}>{county}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Eircode</label>
                      <input
                        type="text"
                        value={editingCustomer.installation_address?.eircode || ''}
                        onChange={(e) => setEditingCustomer({
                          ...editingCustomer,
                          installation_address: { ...editingCustomer.installation_address, eircode: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingCustomer(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Management
const SettingsManager = ({ token, showToast }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const data = await adminApi.getIntegrationSettings(token);
      setSettings(data);
    } catch (err) {
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateIntegrationSettings(token, settings);
      showToast('Settings saved successfully');
    } catch (err) {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
        <p className="text-slate-600">Configure integrations and notifications</p>
      </div>

      {/* SMTP2Go Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          SMTP2Go (Email)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
            <input
              type="password"
              value={settings.smtp2go_api_key || ''}
              onChange={(e) => setSettings({ ...settings, smtp2go_api_key: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Enter API key"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sender Email</label>
            <input
              type="email"
              value={settings.smtp2go_sender_email || ''}
              onChange={(e) => setSettings({ ...settings, smtp2go_sender_email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="noreply@geekbroadband.ie"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sender Name</label>
            <input
              type="text"
              value={settings.smtp2go_sender_name || ''}
              onChange={(e) => setSettings({ ...settings, smtp2go_sender_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="GeekBroadband"
            />
          </div>
        </div>
      </div>

      {/* ClickSend Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5" />
          ClickSend (SMS)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
            <input
              type="text"
              value={settings.clicksend_username || ''}
              onChange={(e) => setSettings({ ...settings, clicksend_username: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Enter username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
            <input
              type="password"
              value={settings.clicksend_api_key || ''}
              onChange={(e) => setSettings({ ...settings, clicksend_api_key: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Enter API key"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sender ID</label>
            <input
              type="text"
              value={settings.clicksend_sender_id || ''}
              onChange={(e) => setSettings({ ...settings, clicksend_sender_id: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="GeekBB"
              maxLength={11}
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Notifications
        </h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Admin Notification Email</label>
          <input
            type="email"
            value={settings.admin_notification_email || ''}
            onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
            placeholder="orders@geekbroadband.ie"
          />
          <p className="text-xs text-slate-500 mt-1">Receive notifications when new orders are placed</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

// Communication Templates Management
const TemplatesManager = ({ token, showToast }) => {
  const [templatesData, setTemplatesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await adminApi.getTemplates(token);
      setTemplatesData(data);
    } catch (err) {
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleToggle = async (templateKey, field) => {
    const template = templatesData.templates[templateKey];
    const newValue = !template[field];
    
    try {
      await adminApi.updateTemplate(token, templateKey, {
        ...template,
        [field]: newValue
      });
      setTemplatesData({
        ...templatesData,
        templates: {
          ...templatesData.templates,
          [templateKey]: {
            ...template,
            [field]: newValue
          }
        }
      });
      showToast(`${field === 'email_enabled' ? 'Email' : 'SMS'} ${newValue ? 'enabled' : 'disabled'}`, 'success');
    } catch (err) {
      showToast('Failed to update template', 'error');
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await adminApi.updateTemplate(token, editingTemplate.key, editingTemplate.data);
      setTemplatesData({
        ...templatesData,
        templates: {
          ...templatesData.templates,
          [editingTemplate.key]: editingTemplate.data
        }
      });
      showToast('Template saved successfully', 'success');
      setEditingTemplate(null);
    } catch (err) {
      showToast('Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Are you sure you want to reset ALL templates to defaults? This cannot be undone.')) return;
    
    setLoading(true);
    try {
      await adminApi.resetTemplates(token);
      await loadTemplates();
      showToast('All templates reset to defaults', 'success');
    } catch (err) {
      showToast('Failed to reset templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetTemplate = async (templateKey, templateName) => {
    if (!window.confirm(`Reset "${templateName}" to default? Your customizations will be lost.`)) return;
    
    try {
      await adminApi.resetTemplates(token, templateKey);
      await loadTemplates();
      showToast(`"${templateName}" reset to default`, 'success');
    } catch (err) {
      showToast('Failed to reset template', 'error');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>;
  }

  const templates = templatesData?.templates || {};
  const templateOrder = [
    'order_confirmation',
    'agreement_request', 
    'agreement_signed',
    'installation_scheduled',
    'service_activated',
    'admin_new_order',
    'admin_agreement_signed'
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Communication Templates</h2>
          <p className="text-slate-600">Customize email and SMS notifications</p>
        </div>
        <button
          onClick={handleResetAll}
          className="px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition text-sm"
        >
          Reset All to Defaults
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h4 className="font-medium text-amber-800 mb-2">Available Variables</h4>
        <p className="text-sm text-amber-700">
          Use these placeholders in your templates: <code className="bg-amber-100 px-1 rounded">{'{{customer_name}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{order_number}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{package_name}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{total_monthly}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{portal_url}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{portal_setup_url}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{install_date}}'}</code>, 
          <code className="bg-amber-100 px-1 rounded">{'{{install_slot}}'}</code>
        </p>
      </div>

      <div className="space-y-4">
        {templateOrder.map((key) => {
          const template = templates[key];
          if (!template) return null;
          
          const isExpanded = expandedTemplate === key;
          const isAdmin = key.startsWith('admin_');
          
          return (
            <div key={key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedTemplate(isExpanded ? null : key)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isAdmin ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    {isAdmin ? <AlertCircle className={`w-5 h-5 ${isAdmin ? 'text-purple-600' : 'text-green-600'}`} /> : 
                     <Send className="w-5 h-5 text-green-600" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <p className="text-sm text-slate-500">{template.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Toggle switches */}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-slate-500">Email</span>
                      <button
                        onClick={() => handleToggle(key, 'email_enabled')}
                        className={`relative w-10 h-6 rounded-full transition ${
                          template.email_enabled ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          template.email_enabled ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-slate-500">SMS</span>
                      <button
                        onClick={() => handleToggle(key, 'sms_enabled')}
                        className={`relative w-10 h-6 rounded-full transition ${
                          template.sms_enabled ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          template.sms_enabled ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </button>
                    </label>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>
              
              {isExpanded && (
                <div className="border-t border-slate-200 p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Subject</label>
                    <input
                      type="text"
                      value={template.email_subject || ''}
                      onChange={(e) => setTemplatesData({
                        ...templatesData,
                        templates: {
                          ...templatesData.templates,
                          [key]: { ...template, email_subject: e.target.value }
                        }
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Body (HTML)</label>
                    <textarea
                      value={template.email_body || ''}
                      onChange={(e) => setTemplatesData({
                        ...templatesData,
                        templates: {
                          ...templatesData.templates,
                          [key]: { ...template, email_body: e.target.value }
                        }
                      })}
                      rows={8}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">This content will be wrapped with GeekBroadband branding automatically.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SMS Body (160 chars max recommended)</label>
                    <textarea
                      value={template.sms_body || ''}
                      onChange={(e) => setTemplatesData({
                        ...templatesData,
                        templates: {
                          ...templatesData.templates,
                          [key]: { ...template, sms_body: e.target.value }
                        }
                      })}
                      rows={2}
                      maxLength={320}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {(template.sms_body || '').length}/160 characters
                      {(template.sms_body || '').length > 160 && <span className="text-amber-600"> (may be split into multiple SMS)</span>}
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => handleResetTemplate(key, template.name)}
                      className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset to Default
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate({ key, data: template });
                        handleSaveTemplate();
                      }}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Template
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============== MAIN ADMIN COMPONENT ==============

const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [username, setUsername] = useState(localStorage.getItem('adminUsername'));
  const [activeTab, setActiveTab] = useState('orders');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const handleLogin = (newToken, newUsername) => {
    setToken(newToken);
    setUsername(newUsername);
    localStorage.setItem('adminToken', newToken);
    localStorage.setItem('adminUsername', newUsername);
  };

  const handleLogout = () => {
    setToken(null);
    setUsername(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
  };

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        const isValid = await adminApi.verify(token);
        if (!isValid) {
          handleLogout();
        }
      }
    };
    verifyToken();
  }, [token]);

  if (!token) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        username={username}
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <main className="flex-1 lg:ml-0">
        {/* Top bar for mobile */}
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 text-slate-600 hover:text-slate-900"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-semibold text-slate-900">GeekBroadband Admin</span>
          <div className="w-10" />
        </div>

        <div className="p-4 lg:p-8">
          {activeTab === 'orders' && <OrdersManager token={token} showToast={showToast} />}
          {activeTab === 'subscribers' && <SubscribersManager token={token} showToast={showToast} />}
          {activeTab === 'packages' && <PackageManager token={token} showToast={showToast} />}
          {activeTab === 'agreement' && <AgreementManager token={token} showToast={showToast} />}
          {activeTab === 'testimonials' && <TestimonialManager token={token} showToast={showToast} />}
          {activeTab === 'faqs' && <FAQManager token={token} showToast={showToast} />}
          {activeTab === 'banners' && <BannerManager token={token} showToast={showToast} />}
          {activeTab === 'legal' && <LegalPagesManager token={token} showToast={showToast} />}
          {activeTab === 'communications' && <TemplatesManager token={token} showToast={showToast} />}
          {activeTab === 'settings' && <SettingsManager token={token} showToast={showToast} />}
        </div>
      </main>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminDashboard;
